import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const Header = () => {
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const NavItem = ({ to, children }) => (
    <NavLink
      to={to}
      data-testid={`nav-${to.replace(/\//g, "") || "home"}`}
      className={({ isActive }) =>
        `link-underline text-sm tracking-wide uppercase ${isActive ? "text-[#A94438]" : "text-[#2C302E]"}`
      }
    >
      {children}
    </NavLink>
  );

  return (
    <header className="sticky top-0 z-40 bg-[#F9F6F0]/85 backdrop-blur border-b border-[#E8E3DA]">
      <div className="mx-auto max-w-7xl px-6 lg:px-12 h-20 flex items-center justify-between">
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>

        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="font-serif text-2xl tracking-tighter text-[#2C302E]">Tienda <em className="text-[#A94438] not-italic">Bonita</em></span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavItem to="/">Inicio</NavItem>
          <NavItem to="/catalogo/ropa">Ropa</NavItem>
          <NavItem to="/catalogo/accesorios">Accesorios</NavItem>
          <NavItem to="/catalogo/regalos">Regalos</NavItem>
          <NavItem to="/catalogo">Todo</NavItem>
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="search-btn"
            onClick={() => navigate("/catalogo")}
            className="hidden md:inline-flex p-2 hover:text-[#A94438]" aria-label="buscar"
          ><Search size={18} /></button>

          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="admin-link" className="p-2 hover:text-[#A94438]" aria-label="admin">
                  <LayoutDashboard size={18} />
                </Link>
              )}
              <Link to="/cuenta" data-testid="account-link" className="p-2 hover:text-[#A94438]"><User size={18} /></Link>
              <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="p-2 hover:text-[#A94438]" aria-label="salir">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="hidden md:inline-flex text-sm uppercase tracking-wide link-underline">
              Ingresar
            </Link>
          )}

          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="cart-toggle"
            className="relative p-2 hover:text-[#A94438]"
            aria-label="carrito"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 h-5 w-5 text-[10px] flex items-center justify-center bg-[#A94438] text-[#F9F6F0] rounded-full">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E8E3DA] bg-[#F9F6F0]">
          <div className="px-6 py-4 flex flex-col gap-4">
            <Link to="/" onClick={() => setOpen(false)} data-testid="m-nav-home">Inicio</Link>
            <Link to="/catalogo/ropa" onClick={() => setOpen(false)} data-testid="m-nav-ropa">Ropa</Link>
            <Link to="/catalogo/accesorios" onClick={() => setOpen(false)} data-testid="m-nav-accesorios">Accesorios</Link>
            <Link to="/catalogo/regalos" onClick={() => setOpen(false)} data-testid="m-nav-regalos">Regalos</Link>
            <Link to="/catalogo" onClick={() => setOpen(false)} data-testid="m-nav-all">Todo</Link>
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" data-testid="m-admin">Admin</Link>}
                <Link to="/cuenta" data-testid="m-cuenta">Mi cuenta</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="text-left" data-testid="m-logout">Salir</button>
              </>
            ) : (
              <Link to="/login" data-testid="m-login">Ingresar</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-[#E8E3DA] bg-[#F2EFE9] mt-24">
    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
      <div className="md:col-span-2">
        <h3 className="font-serif text-3xl tracking-tighter">Tienda <em className="text-[#A94438] not-italic">Bonita</em></h3>
        <p className="mt-4 text-sm text-[#5B605E] max-w-md">
          Boutique en línea de ropa, accesorios y regalos hechos con cariño.
          Piezas de edición limitada que celebran lo cotidiano.
        </p>
      </div>
      <div>
        <h4 className="font-serif text-lg mb-4">Tienda</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/catalogo/ropa" className="link-underline">Ropa</Link></li>
          <li><Link to="/catalogo/accesorios" className="link-underline">Accesorios</Link></li>
          <li><Link to="/catalogo/regalos" className="link-underline">Regalos</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="font-serif text-lg mb-4">Ayuda</h4>
        <ul className="space-y-2 text-sm">
          <li>Envíos & devoluciones</li>
          <li>Guía de tallas</li>
          <li>Contacto: hola@tiendabonita.com</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-[#E8E3DA] py-6 text-center text-xs text-[#5B605E]">
      © {new Date().getFullYear()} Tienda Bonita · Hecho con cariño en México
    </div>
  </footer>
);
